import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { useParams } from 'react-router'
import { spacing, Button } from '@retailer-platform/shared-components'
import { PageHeader } from '@retailer-platform/shared-components'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { useDashMessages } from 'intl/intl.hooks'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { BannerBreadcrumbs } from 'domains/banners/components/banner-breadcrumbs/BannerBreadcrumbs'
import DocumentTitle from 'legacy/components/DocumentTitle'
import { useVisibilityConditionsList } from 'domains/banners/utils/useQueryVisibilityConditionsList.hooks'
import { type VisibilityConditionQueryParams } from 'domains/banners/types/visibilityCondition.types'
import { VisibilityConditionDetail } from 'domains/banners/components/visibility-conditions-list/components/VisibilityConditionDetail'
import { useQueryBanner } from 'domains/banners/utils/useQueryBanner.hooks'

const cardsContainer = css({
  margin: '0 auto',
  paddingTop: spacing.X32,
  paddingRight: spacing.X20,
  paddingBottom: spacing.X32,
  paddingLeft: spacing.X20,
  width: '80%',
})

const createButtonStyle = css({
  paddingBottom: spacing.X32,
})

export const VisibilityConditionsList: FunctionComponent = () => {
  const messages = useDashMessages({
    createButton: 'bannersDomain.visibility-conditions.list.button.create',
  })

  const { placementId } = useParams<VisibilityConditionQueryParams>()

  const { apiSuccess: apiSuccessDetails, apiResult: apiResultDetails } = useQueryBanner({
    placementId: placementId,
  })

  const goToCreatePage = useGoToPath('app-admin-banners-visibility-conditions-create', {
    placementId,
  })

  const { apiLoading, apiError, visibilityConditions } = useVisibilityConditionsList({
    placementId: placementId,
  })

  if (apiError) return <ErrorPage action={() => null} />
  if (apiLoading) return <LoadingPage />

  const placementName =
    apiSuccessDetails && apiResultDetails.name ? apiResultDetails.name : placementId

  return (
    <div>
      <DocumentTitle titleId="bannersDomain.visibility-conditions.list.title" />
      <PageHeader
        addBottomPadding
        title={
          <BannerBreadcrumbs pageType="visibilityConditionsList" placementName={placementName} />
        }
      />
      <div css={cardsContainer}>
        <div css={createButtonStyle}>
          <Button onClick={goToCreatePage}>{messages.createButton}</Button>
        </div>
        {visibilityConditions.map(visibilityCondition => (
          <VisibilityConditionDetail key={visibilityCondition.id} condition={visibilityCondition} />
        ))}
      </div>
    </div>
  )
}
